import {useEffect,useState} from 'react';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SectionIntro from '../components/SectionIntro';
import ServiceCard from '../components/ServiceCard';
import ProjectCard from '../components/ProjectCard';
import GalleryItem from '../components/GalleryItem';
import {AboutSkeleton,GalleryHeroSkeleton,ProjectsSkeleton,ProjectSkeleton,ServicesSkeleton} from '../components/PageSkeletons';
import {galleryItems,processSteps,projects,services} from '../data';
import {fetchAboutPageScalars,fetchGalleryPageScalars,fetchProjectsPageScalars,fetchServicesPageScalars,mapAboutPageScalars,mapGalleryPageScalars,mapProjectsPageScalars,mapServicesPageScalars} from '../services/wordpress';

const {FiArrowUpRight}=FiIcons;

function InnerHero({eyebrow,title,description,image}) {
  return (
    <section className="inner-hero page-width">
      <div><span className="eyebrow">{eyebrow}</span><h1>{title}</h1>{description&&<p>{description}</p>}</div>
      {image&&<img src={image} alt="" />}
    </section>
  );
}

function Cta({eyebrow='Work with us',title,button='Start a Project',url='/contact'}) {
  return <section className="closing-cta page-width inner-cta"><span className="eyebrow">{eyebrow}</span><h2>{title}</h2><Link to={url} className="button button-dark">{button}<SafeIcon icon={FiArrowUpRight} /></Link></section>;
}

const aboutFallback={hero:{eyebrow:'The studio',heading:'Architecture begins with understanding.',description:'FORMA Studio is an architecture and interior design practice creating thoughtful spaces for modern life.',heroImage:'https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1800&q=90'},story:{eyebrow:'Our story',heading:'A studio built around thoughtful design.',lead:'FORMA was founded on the belief that the best spaces are not simply beautiful. They are responsive,enduring and quietly transformative.',body:'We work across architecture,interiors and renovation,bringing a single-minded attention to context,material and the daily rituals of life. Our process is collaborative by nature: we ask careful questions,test ideas openly and make decisions with purpose.'},imageStory:{image:'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1800&q=90',caption:'House in context · Brooklyn,NY'},principles:{eyebrow:'What guides us',heading:'Three principles.'},statement:{text:'“Good spaces should feel inevitable,not imposed.”',attribution:'— FORMA Studio'},closingCta:{eyebrow:'Work with us',heading:"Let's create something meaningful.",label:'Start a Project',url:'/contact'}};

export function About() {
  const [content,setContent]=useState(aboutFallback);
  const [status,setStatus]=useState('loading');
  useEffect(() => {
    let active=true;
    fetchAboutPageScalars().then((meta) => {
      if(active){setContent(mapAboutPageScalars(meta,aboutFallback));setStatus('success');}
    }).catch(() => {if(active){setStatus('error');}});
    return () => {active=false;};
  },[]);
  if(status==='loading') return <AboutSkeleton />;
  return (<><InnerHero eyebrow={content.hero.eyebrow} title={content.hero.heading} description={content.hero.description} image={content.hero.heroImage} /></>);
}

const servicesFallback={hero:{eyebrow:'Our services',heading:'From first idea to finished space.',description:'We bring architectural thinking and interior sensitivity to every scale of project.'},services,process:{eyebrow:'The process',heading:'How We Work'},processSteps,closingCta:{eyebrow:'Work with us',heading:'Every project starts with a conversation.',label:'Discuss Your Project',url:'/contact'}};

export function Services() {
  const [content,setContent]=useState(servicesFallback);
  const [status,setStatus]=useState('loading');
  useEffect(() => {
    let active=true;
    fetchServicesPageScalars().then((meta) => {if(active){setContent(mapServicesPageScalars(meta,servicesFallback));setStatus('success');}}).catch(() => {if(active){setStatus('error');}});
    return () => {active=false;};
  },[]);
  if(status==='loading') return <ServicesSkeleton />;
  return (<><InnerHero eyebrow={content.hero.eyebrow} title={content.hero.heading} description={content.hero.description} /></>);
}

const projectsFallback={hero:{eyebrow:'Selected work',heading:'Projects shaped by people,place and purpose.',description:'A selection of residential,commercial and hospitality spaces made by FORMA Studio.'},projects};

export function Projects() {
  const [content,setContent]=useState(projectsFallback);
  const [status,setStatus]=useState('loading');
  useEffect(() => {
    let active=true;
    fetchProjectsPageScalars().then((meta) => {if(active){setContent(mapProjectsPageScalars(meta,projectsFallback));setStatus('success');}}).catch(() => {if(active){setStatus('error');}});
    return () => {active=false;};
  },[]);
  if(status==='loading') return <ProjectsSkeleton />;
  return (<><InnerHero eyebrow={content.hero.eyebrow} title={content.hero.heading} description={content.hero.description} /><section className="section page-width"><div className="project-grid project-collection">{content.projects.map((project,index)=><ProjectCard key={`${project.title||'project'}-${index}`} project={project} />)}</div></section></>);
}

const galleryFallback={hero:{eyebrow:'Gallery',heading:<>A closer look at spaces,materials and <i>details.</i></>,description:'Fragments of our work,collected for their atmosphere,texture and light.'}};

export function Gallery() {
  const [content,setContent]=useState(galleryFallback);
  const [status,setStatus]=useState('loading');

  useEffect(() => {
    let active=true;
    fetchGalleryPageScalars().then((meta) => {
      if(active){setContent(mapGalleryPageScalars(meta,galleryFallback));setStatus('success');}
    }).catch(() => {
      if(active){setStatus('error');}
    });
    return () => {active=false;};
  },[]);

  if(status==='loading') {
    return (
      <>
        <GalleryHeroSkeleton />
        <section className="section page-width">
          <div className="gallery-grid gallery-collection">
            {galleryItems.map((item,index)=><GalleryItem key={item.title} item={item} index={index} />)}
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <InnerHero eyebrow={content.hero.eyebrow} title={content.hero.heading} description={content.hero.description} />
      <section className="section page-width">
        <div className="gallery-grid gallery-collection">
          {galleryItems.map((item,index)=><GalleryItem key={item.title} item={item} index={index} />)}
        </div>
      </section>
    </>
  );
}